// The answer-time view of a questionnaire. One forward pass over the
// definition expands loop & merge sections, fills carried-forward options,
// applies display conditions and jump rules, and notes the ending reached.
// Mirrors Definition.Resolve in api/internal/survey; the shared
// fixtures/flows.json cases run against both implementations.
import { evalCondition } from './logic';
import { hiddenSections } from './randomize';
import type { Answers, AnswerValue, Definition, Option, Question } from './types';
import { jumpsOf, otherKey } from './types';

export interface Resolved {
  /** Every question as answered (loop copies expanded), in definition order. */
  questions: Question[];
  visible: Record<string, boolean>;
  /** Ending chosen by a jump rule, or null. */
  ending: string | null;
}

export const LOOP_SEPARATOR = '@';

/** The question a loop copy repeats; the id itself for ordinary questions. */
export function baseId(id: string): string {
  const i = id.indexOf(LOOP_SEPARATOR);
  return i < 0 ? id : id.slice(0, i);
}

export function loopSource(def: Definition, q: Question): string | undefined {
  const title = q.sectionTitle?.trim();
  return title ? def.settings.sectionOptions?.[title]?.loopOver : undefined;
}

/** Options picked in a choice answer, in the question's option order. */
function picked(options: Option[], answer: AnswerValue | undefined): Option[] {
  const ids = Array.isArray(answer) ? answer : typeof answer === 'string' ? [answer] : [];
  return options.filter((o) => ids.includes(o.id));
}

/** Option label as the respondent sees it: "Other" becomes the text they typed. */
function shownOption(o: Option, questionId: string, answers: Answers): Option {
  if (!o.other) return o;
  const text = answers[otherKey(questionId)];
  return typeof text === 'string' && text.trim() ? { id: o.id, label: text.trim(), imageUrl: o.imageUrl, score: o.score } : { ...o, other: undefined };
}

/** A loop & merge copy of `q` for one chosen option; references inside the loop point to the same copy. */
function loopCopy(q: Question, inLoop: Set<string>, option: Option): Question {
  const local = (id: string) => (inLoop.has(id) ? `${id}${LOOP_SEPARATOR}${option.id}` : id);
  return {
    ...q,
    id: local(q.id),
    visibleIf: q.visibleIf && { ...q.visibleIf, conditions: q.visibleIf.conditions.map((c) => ({ ...c, questionId: local(c.questionId) })) },
    jumpIf: q.jumpIf && { ...q.jumpIf, targetQuestionId: q.jumpIf.targetQuestionId && local(q.jumpIf.targetQuestionId) },
    jumpRules: q.jumpRules?.map((j) => ({ ...j, targetQuestionId: j.targetQuestionId && local(j.targetQuestionId) })),
    carryForward: q.carryForward && { ...q.carryForward, questionId: local(q.carryForward.questionId) },
    loop: { baseId: q.id, optionId: option.id, label: option.label },
  };
}

export function resolve(def: Definition, answers: Answers): Resolved {
  const questions: Question[] = [];
  const visible: Record<string, boolean> = {};
  const effective: Answers = {};
  const byId = new Map<string, Question>();
  let skipTo: string | null = null;
  let ending: string | null = null;
  // Sections the respondent's randomizer picks left out; a jump that lands in one ends there.
  const unpicked = hiddenSections(def, answers);

  const visit = (q: Question) => {
    byId.set(q.id, q);
    questions.push(q);
    if (skipTo !== null && (q.id === skipTo || q.loop?.baseId === skipTo)) skipTo = null;
    const title = q.sectionTitle?.trim();
    if (ending !== null || skipTo !== null || (title && unpicked.has(title))) {
      visible[q.id] = false;
      return;
    }
    let show = true;
    if (q.carryForward) {
      const src = byId.get(q.carryForward.questionId);
      const all = src?.options ?? [];
      const chosen = picked(all, effective[q.carryForward.questionId]);
      const options = q.carryForward.mode === 'selected' ? chosen : all.filter((o) => !chosen.includes(o));
      q.options = options.map((o) => shownOption(o, q.carryForward!.questionId, answers));
      if (q.options.length === 0) show = false;
    }
    const vi = q.visibleIf;
    if (show && vi && vi.conditions.length > 0) {
      const results = vi.conditions.map((c) => evalCondition(c, effective[c.questionId]));
      show = vi.match === 'any' ? results.some(Boolean) : results.every(Boolean);
    }
    visible[q.id] = show;
    if (!show) return;
    if (q.id in answers) effective[q.id] = answers[q.id] ?? null;
    for (const jump of jumpsOf(q)) {
      if (!evalCondition({ questionId: q.id, operator: jump.operator, value: jump.value }, effective[q.id])) continue;
      if (jump.targetEndingId) ending = jump.targetEndingId;
      else if (jump.targetQuestionId) skipTo = jump.targetQuestionId;
      break;
    }
  };

  for (let i = 0; i < def.questions.length; ) {
    const q = def.questions[i]!;
    const source = loopSource(def, q);
    if (!source) {
      visit({ ...q });
      i++;
      continue;
    }
    const title = q.sectionTitle!.trim();
    const run: Question[] = [];
    while (i < def.questions.length && def.questions[i]!.sectionTitle?.trim() === title) run.push(def.questions[i++]!);
    const inLoop = new Set(run.map((r) => r.id));
    const src = byId.get(source);
    const iterations = src ? picked(src.options ?? [], effective[source]).map((o) => shownOption(o, source, answers)) : [];
    // A jump into a loop that runs zero times lands on the question after it.
    if (iterations.length === 0 && skipTo !== null && inLoop.has(skipTo)) skipTo = null;
    for (const option of iterations) for (const r of run) visit(loopCopy(r, inLoop, option));
  }
  return { questions, visible, ending };
}

/**
 * Evaluates visibility in definition order. Answers to hidden questions are
 * ignored, so hiding cascades to questions that depend on them.
 */
export function visibility(def: Definition, answers: Answers): Record<string, boolean> {
  return resolve(def, answers).visible;
}

/** Option ids a loop can iterate over, in order (following carry-forward back to the original options). */
function loopOptionIds(def: Definition, sourceId: string): string[] {
  const seen = new Set<string>();
  let q = def.questions.find((x) => x.id === sourceId);
  while (q?.carryForward && !seen.has(q.id)) {
    seen.add(q.id);
    const from = q.carryForward.questionId;
    q = def.questions.find((x) => x.id === from);
  }
  return q?.options?.map((o) => o.id) ?? [];
}

type SortKey = [number, number, number];

/**
 * Sort keys for presentation order. `order` lists base question ids (after
 * randomization); loop copies follow their section's position, one pass of
 * the section per chosen option, in the loop question's option order. Works
 * for ids that no longer exist too, so a vanished page can be placed.
 */
export function presentationKey(def: Definition, order: string[]): (id: string) => SortKey {
  const pos = new Map(order.map((id, i) => [id, i]));
  const at = (id: string) => pos.get(id) ?? Number.MAX_SAFE_INTEGER;
  const runStart = new Map<string, number>();
  const loopOf = new Map<string, string>();
  const optionIndex = new Map<string, Map<string, number>>();
  for (const q of def.questions) {
    const source = loopSource(def, q);
    if (!source) continue;
    const title = q.sectionTitle!.trim();
    loopOf.set(q.id, title);
    runStart.set(title, Math.min(runStart.get(title) ?? Number.MAX_SAFE_INTEGER, at(q.id)));
    if (!optionIndex.has(title)) optionIndex.set(title, new Map(loopOptionIds(def, source).map((id, i) => [id, i])));
  }
  return (id) => {
    const base = baseId(id);
    const title = loopOf.get(base);
    if (!title || base === id) return [at(id), 0, 0];
    const option = id.slice(base.length + LOOP_SEPARATOR.length);
    return [runStart.get(title)!, optionIndex.get(title)!.get(option) ?? Number.MAX_SAFE_INTEGER, at(base)];
  };
}

export function compareKeys(a: SortKey, b: SortKey): number {
  return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
}

/** Resolved questions in presentation order (see presentationKey). */
export function orderResolved(def: Definition, order: string[], resolved: Question[]): Question[] {
  const key = presentationKey(def, order);
  return resolved
    .map((q, i) => ({ q, k: key(q.id), i }))
    .sort((a, b) => compareKeys(a.k, b.k) || a.i - b.i)
    .map((x) => x.q);
}

/** Matching quotas (by id) for the answers given so far. */
export function matchingQuotas(def: Definition, answers: Answers): string[] {
  const quotas = def.quotas ?? [];
  if (!quotas.length) return [];
  const r = resolve(def, answers);
  const effective: Answers = {};
  for (const q of r.questions) if (r.visible[q.id] && q.id in answers) effective[q.id] = answers[q.id] ?? null;
  return quotas
    .filter((quota) => {
      if (!quota.conditions.length) return false;
      const results = quota.conditions.map((c) => evalCondition(c, effective[c.questionId]));
      return quota.match === 'any' ? results.some(Boolean) : results.every(Boolean);
    })
    .map((q) => q.id);
}

/**
 * Total score: points of the chosen options of visible questions (choice
 * questions and matrix columns). Null when no option carries points.
 */
export function computeScore(def: Definition, answers: Answers): number | null {
  const scored = def.questions.some((q) => q.options?.some((o) => typeof o.score === 'number'));
  if (!scored) return null;
  const r = resolve(def, answers);
  let total = 0;
  for (const q of r.questions) {
    if (!r.visible[q.id]) continue;
    const v = answers[q.id];
    const points = (id: unknown) => q.options?.find((o) => o.id === id)?.score ?? 0;
    if (q.type === 'single_choice' || q.type === 'dropdown') total += points(v);
    else if (q.type === 'multiple_choice' && Array.isArray(v)) for (const id of new Set(v)) total += points(id);
    else if (q.type === 'matrix' && v && typeof v === 'object' && !Array.isArray(v)) {
      for (const cell of Object.values(v)) {
        if (Array.isArray(cell)) for (const id of new Set(cell)) total += points(id);
        else total += points(cell);
      }
    }
  }
  return Math.round(total * 1000) / 1000;
}
